import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  IndianRupee,
  Loader2,
  MousePointerClick,
  Percent,
  Plus,
  ShoppingCart,
  TrendingUp,
  X,
} from 'lucide-react';
import { adsApi } from '@/api/client';
import {
  AUTOMATIONS_TAB_HOURLY_BUDGET,
  buildAutomationsSearchParams,
} from '@/components/automation/automationsNav';
import { CreateDaypartingAutomationModal } from '@/components/hourlyBudget/CreateDaypartingAutomationModal';
import { Button } from '@/components/ui/Button';
import {
  canCreateDaypartingAutomation,
  DAYPARTING_CREATE_PREFILL_KEY,
  summarizeDaypartingPrefill,
  weeklySlotsFromMatchingHeatmapHours,
} from '@/utils/daypartingAutomationPrefill';
import {
  DaypartingMetricsChart,
  type DaypartingChartPoint,
} from '@/components/DaypartingMetricsChart';
import { DaypartingCampaignFilter } from '@/components/DaypartingCampaignFilter';
import { DaypartingKeywordFilter } from '@/components/DaypartingKeywordFilter';
import { DaypartingPortfolioFilter } from '@/components/DaypartingPortfolioFilter';
import { DaypartingSearchTermFilter } from '@/components/DaypartingSearchTermFilter';
import { DaypartingAsinFilter } from '@/components/DaypartingAsinFilter';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { heatmapCellStyle, heatmapLegendStops, type HeatmapMetricId } from '@/utils/heatmapColors';
import {
  parseHeatmapApiPayload,
  type HeatmapCell,
  type HeatmapDay,
  type HeatmapHour,
  type HeatmapMetrics,
  type ParsedHeatmap,
} from '@/utils/daypartingHeatmapAveraging';
import {
  averagingWeekCount,
  DEFAULT_HEATMAP_VIEW_MODE,
  HEATMAP_VIEW_OPTIONS,
  isAverageHeatmapViewMode,
  heatmapViewWeekLabel,
  shouldFetchWeekChartSeparately,
  type HeatmapViewMode,
} from '@/utils/daypartingHeatmapQueries';
import { isRangeValid, type DateRange } from '@/api/reportDateRanges';

import { formatCurrency as formatAccountCurrency } from '@/utils/formatCurrency';

function formatCurrency(amount: number) {
  return formatAccountCurrency(amount);
}

type DaypartingMetricTotals = {
  impressions: number;
  clicks: number;
  cost: number;
  sales30d: number;
  ctr: number;
  roas: number;
};

function DaypartingMetricCard({
  label,
  value,
  icon,
  iconClassName,
}: {
  label: string;
  value: string;
  icon: ReactNode;
  iconClassName: string;
}) {
  return (
    <div className="flex min-h-[5.5rem] items-center gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm">
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconClassName}`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm text-foreground-subtle">{label}</p>
        <p className="mt-0.5 text-xl font-semibold text-foreground tabular-nums">{value}</p>
      </div>
    </div>
  );
}

const DAYPARTING_METRIC_CARDS: {
  key: string;
  label: string;
  iconClassName: string;
  icon: ReactNode;
  formatValue: (totals: DaypartingMetricTotals) => string;
}[] = [
  {
    key: 'impressions',
    label: 'Impressions',
    iconClassName: 'bg-blue-50 text-blue-600',
    icon: <Eye className="h-5 w-5" aria-hidden />,
    formatValue: (totals) => totals.impressions.toLocaleString('en-IN'),
  },
  {
    key: 'clicks',
    label: 'Clicks',
    iconClassName: 'bg-emerald-50 text-emerald-600',
    icon: <MousePointerClick className="h-5 w-5" aria-hidden />,
    formatValue: (totals) => totals.clicks.toLocaleString('en-IN'),
  },
  {
    key: 'spend',
    label: 'Spend',
    iconClassName: 'bg-orange-50 text-orange-600',
    icon: <IndianRupee className="h-5 w-5" aria-hidden />,
    formatValue: (totals) => formatCurrency(totals.cost),
  },
  {
    key: 'sales',
    label: 'Sales',
    iconClassName: 'bg-violet-50 text-violet-600',
    icon: <ShoppingCart className="h-5 w-5" aria-hidden />,
    formatValue: (totals) => formatCurrency(totals.sales30d),
  },
  {
    key: 'ctr',
    label: 'CTR',
    iconClassName: 'bg-teal-50 text-teal-600',
    icon: <Percent className="h-5 w-5" aria-hidden />,
    formatValue: (totals) => `${totals.ctr.toFixed(2)}%`,
  },
  {
    key: 'roas',
    label: 'ROAS',
    iconClassName: 'bg-amber-50 text-amber-600',
    icon: <TrendingUp className="h-5 w-5" aria-hidden />,
    formatValue: (totals) => totals.roas.toFixed(2),
  },
];

function numberValue(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

type MetricRange = { min: number; max: number };

/** Available heatmap metric columns; default selection is Conversion / CPC / ROAS. */
const METRIC_COLUMNS: Array<{ id: HeatmapMetricId; label: string }> = [
  { id: 'roas', label: 'ROAS' },
  { id: 'acos', label: 'ACoS' },
  { id: 'cpc', label: 'CPC' },
  { id: 'spend', label: 'Spend' },
  { id: 'sales', label: 'Sales (1d)' },
  { id: 'units', label: 'Units (1d)' },
  { id: 'conversion', label: 'Conversion' },
  { id: 'asp', label: 'ASP' },
  { id: 'clicks', label: 'Clicks' },
  { id: 'impressions', label: 'Impr' },
  { id: 'ctr', label: 'CTR' },
];
const DEFAULT_HEATMAP_METRICS: HeatmapMetricId[] = ['conversion', 'cpc', 'roas'];
const WEEKDAY_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
const HEATMAP_WEEK_DAYS = WEEKDAY_ORDER.length;
const HEATMAP_METRIC_BORDER = 'border-r border-border';
const HEATMAP_DAY_GROUP_BORDER = 'border-r-2 border-r-foreground-subtle';
const EMPTY_METRICS: HeatmapMetrics = {
  roas: null,
  acos: null,
  cpc: null,
  spend: null,
  sales: null,
  units: null,
  clicks: null,
  impressions: null,
  ctr: null,
  conversion: null,
  asp: null,
};

type HeatmapFilterOperator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq';

type HeatmapMetricFilter = {
  id: string;
  metric: HeatmapMetricId;
  operator: HeatmapFilterOperator;
  value: number | '';
};

const HEATMAP_FILTER_OPERATORS: Array<{ value: HeatmapFilterOperator; label: string; symbol: string }> = [
  { value: 'gt', label: 'Greater than', symbol: '>' },
  { value: 'gte', label: 'Greater than or equal', symbol: '≥' },
  { value: 'lt', label: 'Less than', symbol: '<' },
  { value: 'lte', label: 'Less than or equal', symbol: '≤' },
  { value: 'eq', label: 'Equal to', symbol: '=' },
  { value: 'neq', label: 'Not equal to', symbol: '≠' },
];

const PERCENT_INPUT_METRICS = new Set<HeatmapMetricId>(['ctr', 'conversion', 'acos']);

function createEmptyHeatmapFilter(): HeatmapMetricFilter {
  return {
    id: crypto.randomUUID(),
    metric: 'roas',
    operator: 'gt',
    value: '',
  };
}

function isCompleteHeatmapFilter(
  filter: HeatmapMetricFilter
): filter is HeatmapMetricFilter & { value: number } {
  return typeof filter.value === 'number' && Number.isFinite(filter.value);
}

/**
 * Convert user-entered filter value to the cell metric scale.
 * Conversion is stored as a 0–1 ratio; CTR from the API is already a percentage.
 */
function filterCompareValue(metric: HeatmapMetricId, value: number): number {
  return metric === 'conversion' ? value / 100 : value;
}

function compareMetricValue(
  cellValue: number,
  operator: HeatmapFilterOperator,
  threshold: number
): boolean {
  switch (operator) {
    case 'gt':
      return cellValue > threshold;
    case 'gte':
      return cellValue >= threshold;
    case 'lt':
      return cellValue < threshold;
    case 'lte':
      return cellValue <= threshold;
    case 'eq':
      return cellValue === threshold;
    case 'neq':
      return cellValue !== threshold;
    default:
      return false;
  }
}

/** True when the cell passes every complete filter (AND). Incomplete filters are ignored. */
function cellMatchesFilters(cell: HeatmapCell, filters: HeatmapMetricFilter[]): boolean {
  const complete = filters.filter(isCompleteHeatmapFilter);
  if (complete.length === 0) return true;

  for (const filter of complete) {
    const cellValue = cell.aggregated[filter.metric];
    if (cellValue === null || !Number.isFinite(cellValue)) return false;
    const threshold = filterCompareValue(filter.metric, filter.value);
    if (!compareMetricValue(cellValue, filter.operator, threshold)) return false;
  }
  return true;
}

function hourLabel(hour: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display} ${suffix}`;
}

function formatCompactNumber(value: number, maxFractionDigits = 1): string {
  return new Intl.NumberFormat('en-IN', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
}

function formatMetricValue(metricId: HeatmapMetricId, value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (metricId === 'spend' || metricId === 'sales' || metricId === 'cpc' || metricId === 'asp') {
    return formatCurrency(value);
  }
  if (metricId === 'units') return formatCompactNumber(value, 1);
  // CTR from the heatmap API is already a percentage; conversion is a 0–1 ratio.
  if (metricId === 'ctr' || metricId === 'acos') return `${value.toFixed(2)}%`;
  if (metricId === 'conversion') return `${(value * 100).toFixed(2)}%`;
  if (metricId === 'roas') return value.toFixed(2);
  return formatCompactNumber(value, 0);
}

/** Derive conversion (units/clicks), ASP (sales/units), and ACoS (spend/sales) from base heatmap metrics. */
function deriveHeatmapMetrics(source: Partial<HeatmapMetrics>): HeatmapMetrics {
  const base = { ...EMPTY_METRICS, ...source };
  const { clicks, units, sales, spend } = base;
  return {
    ...base,
    conversion:
      clicks !== null && clicks > 0 && units !== null && Number.isFinite(units) ? units / clicks : null,
    asp: units !== null && units > 0 && sales !== null && Number.isFinite(sales) ? sales / units : null,
    acos:
      sales !== null && sales > 0 && spend !== null && Number.isFinite(spend) ? (spend / sales) * 100 : null,
  };
}

/** Sum hourly cells for one day; recompute CTR, CPC, ROAS, and ACoS from totals. */
function computeDayColumnTotals(cells: HeatmapCell[]): HeatmapMetrics {
  if (!cells.some((cell) => cell.rowCount > 0)) return { ...EMPTY_METRICS };

  const sum = (id: keyof Pick<HeatmapMetrics, 'impressions' | 'clicks' | 'spend' | 'sales' | 'units'>) =>
    cells.reduce((total, cell) => {
      const value = cell.aggregated[id];
      return value !== null && Number.isFinite(value) ? total + value : total;
    }, 0);

  const impressions = sum('impressions');
  const clicks = sum('clicks');
  const spend = sum('spend');
  const sales = sum('sales');
  const units = sum('units');

  return deriveHeatmapMetrics({
    impressions,
    clicks,
    spend,
    sales,
    units,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : null,
    cpc: clicks > 0 ? spend / clicks : null,
    roas: spend > 0 ? sales / spend : null,
  });
}

function localDateKey(value: Date): string {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateFromLocalKey(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function formatShortDate(value: string): string {
  const date = dateFromLocalKey(value) ?? new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' }).format(date);
}

function HeatmapSkeletonCell({ className = '' }: { className?: string }) {
  return (
    <td className={`bg-surface-muted/80 px-2 py-2 ${className}`}>
      <span
        className="ml-auto block h-4 w-14 max-w-full animate-pulse rounded-md bg-[#c8c8cc]"
        aria-hidden
      />
    </td>
  );
}

function normalizeHeatmapTimezone(value: string | undefined | null): string {
  const requested = (value || '').trim();
  if (!requested) return 'UTC';
  // Canonicalize legacy alias often returned by some runtimes in India.
  if (requested === 'Asia/Calcutta') return 'Asia/Kolkata';
  return requested;
}

/** Local Monday 00:00 for the week containing `value`. */
function startOfWeekMonday(value: Date): Date {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  const day = result.getDay(); // 0 = Sunday
  const offset = day === 0 ? -6 : 1 - day;
  result.setDate(result.getDate() + offset);
  return result;
}

/** Local Sunday 23:59 for the week starting at `monday`. */
function endOfWeekSunday(monday: Date): Date {
  const result = new Date(monday);
  result.setDate(result.getDate() + 6);
  result.setHours(23, 59, 0, 0);
  return result;
}

type StreamTimeRange = {
  startIso: string;
  endIso: string;
  label: string;
  dayCount: number;
  isValid: boolean;
  capped: boolean;
};

function inclusiveLocalDayCount(start: Date, end: Date): number {
  const startDay = dateFromLocalKey(localDateKey(start));
  const endDay = dateFromLocalKey(localDateKey(end));
  if (!startDay || !endDay) return 1;
  const diffMs = endDay.getTime() - startDay.getTime();
  return Math.max(1, Math.floor(diffMs / 86400000) + 1);
}

/** Local start-of-day / end-of-day bounds for a YYYY-MM-DD range. */
function buildSelectedRangeFromDates(startDate: string, endDate: string): StreamTimeRange {
  const start = dateFromLocalKey(startDate);
  const end = dateFromLocalKey(endDate);
  const datesValid = Boolean(start && end);
  const isValid = datesValid && isRangeValid({ startDate, endDate });
  if (!isValid || !start || !end) {
    return {
      startIso: new Date().toISOString(),
      endIso: new Date().toISOString(),
      label: 'Invalid range',
      dayCount: 1,
      isValid: false,
      capped: false,
    };
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  const dayCount = inclusiveLocalDayCount(start, end);
  return {
    startIso: start.toISOString(),
    endIso: end.toISOString(),
    label: `${formatShortDate(startDate)} – ${formatShortDate(endDate)}`,
    dayCount,
    isValid: true,
    capped: false,
  };
}

/** Snap heatmap to the Mon–Sun week that contains the selected range end. */
function buildHeatmapWeekRange(selectedRange: StreamTimeRange): StreamTimeRange {
  if (!selectedRange.isValid) {
    return { ...selectedRange, dayCount: HEATMAP_WEEK_DAYS };
  }
  const monday = startOfWeekMonday(new Date(selectedRange.endIso));
  const sunday = endOfWeekSunday(monday);
  const startKey = localDateKey(monday);
  const endKey = localDateKey(sunday);
  return {
    startIso: monday.toISOString(),
    endIso: sunday.toISOString(),
    label: `${formatShortDate(startKey)} – ${formatShortDate(endKey)} · Mon–Sun`,
    dayCount: HEATMAP_WEEK_DAYS,
    isValid: true,
    capped: false,
  };
}

function shiftToWeekDates(anchor: Date, deltaWeeks: number): DateRange {
  const monday = startOfWeekMonday(anchor);
  monday.setDate(monday.getDate() + deltaWeeks * 7);
  const sunday = endOfWeekSunday(monday);
  const now = new Date();
  const end = sunday > now ? now : sunday;
  return {
    startDate: localDateKey(monday),
    endDate: localDateKey(end),
  };
}

function buildChartPointsFromHeatmap(payload: Record<string, unknown> | undefined): DaypartingChartPoint[] {
  const points: DaypartingChartPoint[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    cpc: null,
    roas: null,
  }));
  if (!payload) return points;

  const payloadHours = Array.isArray(payload.hours) ? payload.hours : [];
  payloadHours.forEach((hourValue) => {
    const hourEntry = hourValue as Record<string, unknown>;
    const hour = numberValue(hourEntry.hour);
    if (hour < 0 || hour > 23) return;

    let totalSpend = 0;
    let totalClicks = 0;
    let totalSales = 0;
    const rawCells = Array.isArray(hourEntry.cells) ? hourEntry.cells : [];
    rawCells.forEach((cellValue) => {
      const cell = cellValue as Record<string, unknown>;
      const aggregated = (cell.aggregated ?? {}) as Record<string, unknown>;
      totalSpend += numberValue(aggregated.spend);
      totalClicks += numberValue(aggregated.clicks);
      totalSales += numberValue(aggregated.sales);
    });

    points[hour] = {
      hour,
      cpc: totalClicks > 0 ? totalSpend / totalClicks : null,
      roas: totalSpend > 0 ? totalSales / totalSpend : null,
    };
  });

  return points;
}

export type StreamInsightsSource = 'v1' | 'v2';

export function streamInsightsFilterParams(
  source: StreamInsightsSource,
  selected: {
    campaignIds: string[];
    portfolioIds: string[];
    keywordIds: string[];
    searchTerms: string[];
    asins: string[];
  }
): {
  campaignIds?: string[];
  portfolioIds?: string[];
  keywordIds?: string[];
  searchTerms?: string[];
  asins?: string[];
} {
  const params: {
    campaignIds?: string[];
    portfolioIds?: string[];
    keywordIds?: string[];
    searchTerms?: string[];
    asins?: string[];
  } = {};
  if (selected.campaignIds.length > 0) params.campaignIds = selected.campaignIds;
  else if (selected.portfolioIds.length > 0) params.portfolioIds = selected.portfolioIds;
  if (source === 'v2') {
    if (selected.keywordIds.length > 0) params.keywordIds = selected.keywordIds;
    if (selected.searchTerms.length > 0) params.searchTerms = selected.searchTerms;
    if (selected.asins.length > 0) params.asins = selected.asins;
  }
  return params;
}

export function StreamInsightsTab({
  profileId,
  source = 'v1',
}: {
  profileId: string;
  source?: StreamInsightsSource;
}) {
  const navigate = useNavigate();
  const browserTimezone = normalizeHeatmapTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const { range, setCurrentRange } = useAppDateRange();
  const dateRange = range.current;
  const [createAutomationOpen, setCreateAutomationOpen] = useState(false);

  const [selectedHeatmapMetrics, setSelectedHeatmapMetrics] =
    useState<HeatmapMetricId[]>(DEFAULT_HEATMAP_METRICS);
  const [heatmapColumnsOpen, setHeatmapColumnsOpen] = useState(false);
  const heatmapColumnsRef = useRef<HTMLDivElement>(null);
  const [heatmapViewMode, setHeatmapViewMode] =
    useState<HeatmapViewMode>(DEFAULT_HEATMAP_VIEW_MODE);
  const [heatmapFilters, setHeatmapFilters] = useState<HeatmapMetricFilter[]>(() => [
    createEmptyHeatmapFilter(),
  ]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<string[]>([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState<string[]>([]);
  const [selectedSearchTerms, setSelectedSearchTerms] = useState<string[]>([]);
  const [selectedAsins, setSelectedAsins] = useState<string[]>([]);

  const handleCampaignIdsChange = (campaignIds: string[]) => {
    setSelectedCampaignIds(campaignIds);
    if (campaignIds.length > 0) setSelectedPortfolioIds([]);
  };

  const handlePortfolioIdsChange = (portfolioIds: string[]) => {
    setSelectedPortfolioIds(portfolioIds);
    if (portfolioIds.length > 0) setSelectedCampaignIds([]);
  };

  const campaignIdsQueryKey = useMemo(
    () => [...selectedCampaignIds].sort().join(','),
    [selectedCampaignIds]
  );

  const portfolioIdsQueryKey = useMemo(
    () => [...selectedPortfolioIds].sort().join(','),
    [selectedPortfolioIds]
  );

  const keywordIdsQueryKey = useMemo(
    () => [...selectedKeywordIds].sort().join('\n'),
    [selectedKeywordIds]
  );

  const searchTermsQueryKey = useMemo(
    () => [...selectedSearchTerms].sort().join('\n'),
    [selectedSearchTerms]
  );

  const asinsQueryKey = useMemo(
    () => [...selectedAsins].sort().join('\n'),
    [selectedAsins]
  );

  const campaignReplaceLabel =
    selectedCampaignIds.length === 0
      ? null
      : selectedCampaignIds.length === 1
        ? '1 campaign'
        : `${selectedCampaignIds.length} campaigns`;

  const portfolioReplaceLabel =
    selectedPortfolioIds.length === 0
      ? null
      : selectedPortfolioIds.length === 1
        ? '1 portfolio'
        : `${selectedPortfolioIds.length} portfolios`;

  const streamEntityFilterParams = useMemo(
    () =>
      streamInsightsFilterParams(source, {
        campaignIds: selectedCampaignIds,
        portfolioIds: selectedPortfolioIds,
        keywordIds: selectedKeywordIds,
        searchTerms: selectedSearchTerms,
        asins: selectedAsins,
      }),
    [
      selectedAsins,
      selectedCampaignIds,
      selectedKeywordIds,
      selectedPortfolioIds,
      selectedSearchTerms,
      source,
    ]
  );

  useEffect(() => {
    if (!heatmapColumnsOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (heatmapColumnsRef.current && !heatmapColumnsRef.current.contains(event.target as Node)) {
        setHeatmapColumnsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setHeatmapColumnsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [heatmapColumnsOpen]);

  const selectedRange = useMemo(
    () => buildSelectedRangeFromDates(dateRange.startDate, dateRange.endDate),
    [dateRange.startDate, dateRange.endDate]
  );

  const heatmapRange = useMemo(() => buildHeatmapWeekRange(selectedRange), [selectedRange]);

  const canGoNextWeek = useMemo(() => {
    if (!selectedRange.isValid) return false;
    const currentWeekMonday = startOfWeekMonday(new Date(selectedRange.endIso));
    const thisWeekMonday = startOfWeekMonday(new Date());
    return currentWeekMonday.getTime() < thisWeekMonday.getTime();
  }, [selectedRange]);

  const goToAdjacentWeek = (deltaWeeks: number) => {
    const anchor = selectedRange.isValid ? new Date(selectedRange.endIso) : new Date();
    setCurrentRange(shiftToWeekDates(anchor, deltaWeeks));
  };

  const summaryQuery = useQuery({
    queryKey: [
      'stream-summary',
      source,
      profileId,
      dateRange.startDate,
      dateRange.endDate,
      campaignIdsQueryKey,
      portfolioIdsQueryKey,
      keywordIdsQueryKey,
      searchTermsQueryKey,
      asinsQueryKey,
    ],
    queryFn: async () => {
      const range = buildSelectedRangeFromDates(dateRange.startDate, dateRange.endDate);
      const loadSummary = source === 'v2' ? adsApi.getStreamV2Summary : adsApi.getStreamSummary;
      const res = await loadSummary(profileId, {
        startTime: range.startIso,
        endTime: range.endIso,
        ...streamEntityFilterParams,
      });
      if (!res.success) throw new Error('Failed to load stream summary');
      return res.data;
    },
    enabled: Boolean(profileId) && selectedRange.isValid,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const averagingWeeks = averagingWeekCount(heatmapViewMode);
  const isAverageView = isAverageHeatmapViewMode(heatmapViewMode);

  const heatmapQuery = useQuery({
    queryKey: [
      'stream-heatmap',
      source,
      profileId,
      heatmapRange.startIso,
      heatmapRange.endIso,
      heatmapRange.dayCount,
      browserTimezone,
      heatmapViewMode,
      campaignIdsQueryKey,
      portfolioIdsQueryKey,
      keywordIdsQueryKey,
      searchTermsQueryKey,
      asinsQueryKey,
    ],
    queryFn: async () => {
      const loadHeatmap = source === 'v2' ? adsApi.getStreamV2Heatmap : adsApi.getStreamHeatmap;
      const res = await loadHeatmap(profileId, {
        startTime: heatmapRange.startIso,
        endTime: heatmapRange.endIso,
        timezone: browserTimezone,
        days: heatmapRange.dayCount,
        view: heatmapViewMode,
        ...streamEntityFilterParams,
      });
      if (!res.success) throw new Error('Failed to load stream heatmap');
      return (res.data as Record<string, unknown>) ?? {};
    },
    enabled: Boolean(profileId) && heatmapRange.isValid,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const fetchWeekChartSeparately = shouldFetchWeekChartSeparately(heatmapViewMode);

  const chartQuery = useQuery({
    queryKey: [
      'stream-chart',
      source,
      profileId,
      heatmapRange.startIso,
      heatmapRange.endIso,
      heatmapRange.dayCount,
      browserTimezone,
      campaignIdsQueryKey,
      portfolioIdsQueryKey,
      keywordIdsQueryKey,
      searchTermsQueryKey,
      asinsQueryKey,
    ],
    queryFn: async () => {
      const loadHeatmap = source === 'v2' ? adsApi.getStreamV2Heatmap : adsApi.getStreamHeatmap;
      const res = await loadHeatmap(profileId, {
        startTime: heatmapRange.startIso,
        endTime: heatmapRange.endIso,
        timezone: browserTimezone,
        days: heatmapRange.dayCount,
        view: 'normal',
        ...streamEntityFilterParams,
      });
      if (!res.success) throw new Error('Failed to load stream chart data');
      return (res.data as Record<string, unknown>) ?? {};
    },
    // Average grids use a widened window; keep the chart on the selected week.
    enabled: Boolean(profileId) && heatmapRange.isValid && fetchWeekChartSeparately,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const chartSource = fetchWeekChartSeparately ? chartQuery.data : heatmapQuery.data;
  const chartPoints = useMemo(
    () => buildChartPointsFromHeatmap(chartSource as Record<string, unknown> | undefined),
    [chartSource]
  );
  const chartHasData = Boolean(chartSource);
  const chartLoading = fetchWeekChartSeparately
    ? (chartQuery.isPending || chartQuery.isFetching) && !chartHasData
    : (heatmapQuery.isPending || heatmapQuery.isFetching) && !chartHasData;
  const chartError = fetchWeekChartSeparately ? chartQuery.isError : heatmapQuery.isError;
  const chartTimezone = String(
    (chartSource as Record<string, unknown> | undefined)?.timezone || browserTimezone
  );

  const totals = useMemo(() => {
    const t = (summaryQuery.data as Record<string, unknown> | undefined)?.totals as
      | Record<string, unknown>
      | undefined;
    return {
      impressions: numberValue(t?.impressions),
      clicks: numberValue(t?.clicks),
      cost: numberValue(t?.cost),
      sales30d: numberValue(t?.sales30d),
      ctr: numberValue(t?.ctr),
      roas: numberValue(t?.roas),
    };
  }, [summaryQuery.data]);

  const emptyHeatmap = useMemo((): ParsedHeatmap => {
    const monday = startOfWeekMonday(new Date(heatmapRange.startIso));
    const days: HeatmapDay[] = WEEKDAY_ORDER.map((dayName, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      return { day: dayName, date: localDateKey(date) };
    });
    const emptyCells: HeatmapCell[] = days.map((day) => ({
      day: day.day,
      date: day.date,
      rowCount: 0,
      aggregated: { ...EMPTY_METRICS },
    }));
    const hours: HeatmapHour[] = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      cells: emptyCells.map((cell) => ({ ...cell, aggregated: { ...EMPTY_METRICS } })),
    }));
    return {
      days,
      hours,
      activeBuckets: 0,
      rowCount: 0,
      timezone: browserTimezone,
      dayCount: HEATMAP_WEEK_DAYS,
    };
  }, [browserTimezone, heatmapRange.startIso]);

  const heatmap = useMemo(() => {
    const payload = heatmapQuery.data as Record<string, unknown> | undefined;
    if (!payload) return emptyHeatmap;
    return parseHeatmapApiPayload(payload, heatmapRange, browserTimezone);
  }, [browserTimezone, emptyHeatmap, heatmapQuery.data, heatmapRange]);

  const activeFilterCount = useMemo(
    () => heatmapFilters.filter(isCompleteHeatmapFilter).length,
    [heatmapFilters]
  );

  const filteredHeatmap = useMemo(() => {
    if (activeFilterCount === 0) return heatmap;

    let activeBuckets = 0;
    const hours = heatmap.hours.map((hourEntry) => ({
      ...hourEntry,
      cells: hourEntry.cells.map((cell) => {
        if (cellMatchesFilters(cell, heatmapFilters)) {
          if (cell.rowCount > 0) activeBuckets += 1;
          return cell;
        }
        return {
          ...cell,
          rowCount: 0,
          aggregated: { ...EMPTY_METRICS },
        };
      }),
    }));

    return { ...heatmap, hours, activeBuckets };
  }, [activeFilterCount, heatmap, heatmapFilters]);

  const matchingWeeklySlots = useMemo(
    () => weeklySlotsFromMatchingHeatmapHours(filteredHeatmap.hours),
    [filteredHeatmap.hours]
  );

  const canCreateAutomation = canCreateDaypartingAutomation({
    completeFilterCount: activeFilterCount,
    weeklySlots: matchingWeeklySlots,
  });

  const prefillSummary = useMemo(
    () => summarizeDaypartingPrefill(matchingWeeklySlots),
    [matchingWeeklySlots]
  );

  const singleCampaignId =
    selectedCampaignIds.length === 1 ? selectedCampaignIds[0] : undefined;
  const heatmapPortfolioId =
    selectedPortfolioIds.length === 1 ? selectedPortfolioIds[0] : undefined;

  const openCreateAutomation = () => {
    if (!canCreateAutomation) return;
    setCreateAutomationOpen(true);
  };

  const confirmCreateAutomation = () => {
    const search = new URLSearchParams(
      buildAutomationsSearchParams(
        { tab: '', entity: '', rule: '', section: '' },
        { tab: AUTOMATIONS_TAB_HOURLY_BUDGET }
      )
    ).toString();
    setCreateAutomationOpen(false);
    navigate(
      {
        pathname: `/accounts/${encodeURIComponent(profileId)}/campaigns/automations`,
        search,
      },
      {
        state: {
          [DAYPARTING_CREATE_PREFILL_KEY]: {
            weeklySlots: matchingWeeklySlots,
            campaignId: singleCampaignId,
            campaignIds: selectedCampaignIds.length > 0 ? selectedCampaignIds : undefined,
            portfolioId: heatmapPortfolioId,
          },
        },
      }
    );
  };

  const { metricRanges, metricThresholdHigh } = useMemo(() => {
    const ranges = {} as Record<HeatmapMetricId, MetricRange | null>;
    const thresholds = {} as Record<HeatmapMetricId, number | null>;

    for (const { id } of METRIC_COLUMNS) {
      const values: number[] = [];

      for (const hour of filteredHeatmap.hours) {
        for (const cell of hour.cells) {
          const value = cell.aggregated[id];
          if (value !== null && Number.isFinite(value)) values.push(value);
        }
      }

      ranges[id] = values.length ? { min: Math.min(...values), max: Math.max(...values) } : null;

      // Account-specific threshold: Sales high if value is >= median of visible cells.
      if (id === 'sales') {
        if (values.length) {
          const sorted = [...values].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          thresholds[id] =
            sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
        } else {
          thresholds[id] = null;
        }
      }
    }

    // Fixed thresholds: ROAS 2.0 and the matching ACoS of 50%.
    thresholds.roas = 2.0;
    thresholds.acos = 50;

    // Default any other metric thresholds to null (no threshold bias).
    for (const { id } of METRIC_COLUMNS) {
      if (thresholds[id] === undefined) thresholds[id] = null;
    }

    return { metricRanges: ranges, metricThresholdHigh: thresholds };
  }, [filteredHeatmap.hours]);

  const selectedMetricColumns = useMemo(() => {
    const selectedSet = new Set(selectedHeatmapMetrics);
    const visible = METRIC_COLUMNS.filter((metric) => selectedSet.has(metric.id));
    return visible.length > 0 ? visible : METRIC_COLUMNS.filter((metric) => metric.id === 'cpc');
  }, [selectedHeatmapMetrics]);

  const isAverageHeatmapView = isAverageView;

  /** Max contributing weeks per weekday column (for day-header "Avg · Nw"). */
  const daySampleCounts = useMemo(() => {
    if (!isAverageHeatmapView) return [] as number[];
    return filteredHeatmap.days.map((_, dayIndex) => {
      let maxSamples = 0;
      for (const hourEntry of filteredHeatmap.hours) {
        const cell = hourEntry.cells[dayIndex];
        if (cell && cell.rowCount > maxSamples) maxSamples = cell.rowCount;
      }
      return maxSamples;
    });
  }, [filteredHeatmap.days, filteredHeatmap.hours, isAverageHeatmapView]);

  const heatmapWeekLabel = heatmapViewWeekLabel(heatmapViewMode, heatmapRange.label);

  const dayColumnTotals = useMemo(() => {
    if (filteredHeatmap.hours.length === 0) return [];
    const dayCount = filteredHeatmap.hours[0]?.cells.length ?? 0;
    return Array.from({ length: dayCount }, (_, dayIndex) =>
      computeDayColumnTotals(filteredHeatmap.hours.map((hour) => hour.cells[dayIndex]).filter(Boolean))
    );
  }, [filteredHeatmap.hours]);

  const heatmapLoading = heatmapQuery.isPending || heatmapQuery.isFetching;
  const heatmapQuerySuccess = heatmapQuery.isSuccess;
  const heatmapError = heatmapQuery.isError ? heatmapQuery.error : undefined;
  const heatmapBucketTotal = filteredHeatmap.dayCount * 24;
  // Keep the grid chrome visible while fetching; only skeleton when we have no usable rows yet.
  const showHeatmapSkeleton = heatmapLoading && heatmap.rowCount === 0;
  const showHeatmapTable =
    selectedRange.isValid && !heatmapError && (showHeatmapSkeleton || heatmap.rowCount > 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {summaryQuery.isPending || summaryQuery.isFetching ? (
          <div className="col-span-full flex items-center gap-2 text-sm text-foreground-muted">
            <Loader2 className="w-4 h-4 animate-spin text-brand" />
            Loading stream summary…
          </div>
        ) : null}
        {summaryQuery.isError ? (
          <div className="col-span-full text-sm text-error">
            Failed to load stream summary. Totals below may be empty until you refresh.
          </div>
        ) : null}
        {DAYPARTING_METRIC_CARDS.map((metric) => (
          <DaypartingMetricCard
            key={metric.key}
            label={metric.label}
            value={metric.formatValue(totals)}
            icon={metric.icon}
            iconClassName={metric.iconClassName}
          />
        ))}
      </div>

      {!selectedRange.isValid ? (
        <p className="text-sm text-error">End time must be on or after start time.</p>
      ) : null}

      <DaypartingMetricsChart
        points={chartPoints}
        loading={chartLoading}
        error={chartError || !heatmapRange.isValid}
        subtitle={`${heatmapRange.label} · Timezone: ${normalizeHeatmapTimezone(chartTimezone)}`}
      />

      <div className="space-y-2">
        <p className="text-xs text-foreground-subtle">
          {source === 'v2'
            ? 'Filter by campaigns or portfolios, one type at a time, plus optional keywords, search terms, and ASINs.'
            : 'Filter by campaigns or portfolios. Only one type can be applied at a time.'}
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          <DaypartingCampaignFilter
            profileId={profileId}
            selectedCampaignIds={selectedCampaignIds}
            onChange={handleCampaignIdsChange}
            replacesFilterLabel={portfolioReplaceLabel}
          />
          <DaypartingPortfolioFilter
            profileId={profileId}
            selectedPortfolioIds={selectedPortfolioIds}
            onChange={handlePortfolioIdsChange}
            replacesFilterLabel={campaignReplaceLabel}
          />
          {source === 'v2' ? (
            <>
              <DaypartingKeywordFilter
                profileId={profileId}
                campaignIds={selectedCampaignIds}
                selectedKeywordIds={selectedKeywordIds}
                onChange={setSelectedKeywordIds}
              />
              <DaypartingSearchTermFilter
                profileId={profileId}
                campaignIds={selectedCampaignIds}
                keywordIds={selectedKeywordIds}
                selectedSearchTerms={selectedSearchTerms}
                onChange={setSelectedSearchTerms}
              />
              <DaypartingAsinFilter
                profileId={profileId}
                campaignIds={selectedCampaignIds}
                keywordIds={selectedKeywordIds}
                searchTerms={selectedSearchTerms}
                selectedAsins={selectedAsins}
                onChange={setSelectedAsins}
              />
            </>
          ) : null}
        </div>
      </div>

      <section className="bg-surface border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-foreground">Hourly heatmap</h4>
          {heatmapLoading ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-brand" />
              Loading…
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => goToAdjacentWeek(-1)}
              className="px-2 py-1.5 text-xs text-foreground-muted hover:bg-page"
              title="Previous week"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              disabled={!canGoNextWeek}
              onClick={() => goToAdjacentWeek(1)}
              className="px-2 py-1.5 text-xs text-foreground-muted border-l border-border hover:bg-page disabled:opacity-40"
              title="Next week"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="inline-flex items-center rounded-lg border border-border overflow-hidden">
            {HEATMAP_VIEW_OPTIONS.map((option, index) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setHeatmapViewMode(option.id)}
                className={`px-3 py-1.5 text-xs ${index > 0 ? 'border-l border-border' : ''} ${
                  heatmapViewMode === option.id
                    ? 'bg-page text-foreground font-medium'
                    : 'text-foreground-muted hover:bg-page'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div ref={heatmapColumnsRef} className="relative">
            <button
              type="button"
              aria-expanded={heatmapColumnsOpen}
              aria-haspopup="true"
              onClick={() => setHeatmapColumnsOpen((value) => !value)}
              className="rounded-pill border border-border bg-surface px-3 py-1.5 text-xs text-foreground-muted hover:bg-page"
            >
              Columns ({selectedMetricColumns.length})
            </button>
            {heatmapColumnsOpen ? (
              <div className="absolute left-0 z-30 mt-2 w-44 rounded-xl border border-border bg-surface p-2 shadow-lg">
                <p className="mb-2 text-[11px] uppercase tracking-wide text-foreground-subtle">Heatmap columns</p>
                <div className="space-y-1.5">
                  {METRIC_COLUMNS.map((metric) => {
                    const checked = selectedHeatmapMetrics.includes(metric.id);
                    const disableUncheck = checked && selectedHeatmapMetrics.length === 1;
                    return (
                      <label key={metric.id} className="flex items-center gap-2 text-xs text-foreground-muted">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disableUncheck}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setSelectedHeatmapMetrics((prev) => {
                              if (isChecked) {
                                return prev.includes(metric.id) ? prev : [...prev, metric.id];
                              }
                              if (prev.length <= 1) return prev;
                              return prev.filter((id) => id !== metric.id);
                            });
                          }}
                        />
                        {metric.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
          <div className="text-xs text-foreground-subtle">Timezone: {filteredHeatmap.timezone}</div>
          <div className="text-xs text-foreground-subtle">{heatmapWeekLabel}</div>
        </div>

        <div className="rounded-xl border border-border bg-page/60 p-3 space-y-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h5 className="text-xs font-semibold text-foreground">Filters</h5>
              <span className="text-[11px] text-foreground-subtle">
                {activeFilterCount > 0
                  ? `${activeFilterCount} active · match all (AND)`
                  : 'Match all conditions (AND) · all cells shown until a value is set'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {heatmapFilters.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setHeatmapFilters([createEmptyHeatmapFilter()])}
                  className="text-[11px] text-foreground-muted hover:text-foreground"
                >
                  Clear all
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setHeatmapFilters((prev) => [...prev, createEmptyHeatmapFilter()])}
                className="inline-flex items-center gap-1 rounded-pill border border-border bg-surface px-2.5 py-1 text-xs text-foreground-muted hover:text-foreground"
              >
                <Plus className="w-3.5 h-3.5" />
                Add filter
              </button>
              <Button
                size="sm"
                disabled={!canCreateAutomation}
                title={
                  canCreateAutomation
                    ? 'Create a dayparting automation from matching hours'
                    : 'Add a complete filter with matching hours first'
                }
                onClick={openCreateAutomation}
              >
                Create automation
              </Button>
            </div>
          </div>

          {heatmapFilters.length === 0 ? (
            <p className="text-xs text-foreground-subtle">
              No filters yet. Add a condition to show only matching heatmap cells.
            </p>
          ) : (
            <div className="space-y-2">
              {heatmapFilters.map((filter, index) => (
                <div key={filter.id} className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  <span className="w-8 shrink-0 text-[10px] font-medium uppercase tracking-wide text-foreground-subtle">
                    {index > 0 ? 'and' : 'if'}
                  </span>
                  <select
                    className="min-w-[7.5rem] flex-1 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs sm:flex-none"
                    value={filter.metric}
                    aria-label="Filter metric"
                    onChange={(e) => {
                      const metric = e.target.value as HeatmapMetricId;
                      setHeatmapFilters((prev) =>
                        prev.map((row) => (row.id === filter.id ? { ...row, metric } : row))
                      );
                    }}
                  >
                    {METRIC_COLUMNS.map((metric) => (
                      <option key={metric.id} value={metric.id}>
                        {metric.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="min-w-[9.5rem] flex-1 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs sm:flex-none"
                    value={filter.operator}
                    aria-label="Filter operator"
                    onChange={(e) => {
                      const operator = e.target.value as HeatmapFilterOperator;
                      setHeatmapFilters((prev) =>
                        prev.map((row) => (row.id === filter.id ? { ...row, operator } : row))
                      );
                    }}
                  >
                    {HEATMAP_FILTER_OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.symbol} {op.label}
                      </option>
                    ))}
                  </select>
                  <div className="relative">
                    <input
                      type="number"
                      className={`w-24 rounded-lg border border-border bg-surface py-1.5 text-xs ${
                        PERCENT_INPUT_METRICS.has(filter.metric) ? 'pl-2.5 pr-6' : 'px-2.5'
                      }`}
                      value={filter.value}
                      placeholder="Value"
                      aria-label="Filter value"
                      onChange={(e) => {
                        const raw = e.target.value;
                        const value = raw === '' ? '' : Number(raw);
                        setHeatmapFilters((prev) =>
                          prev.map((row) =>
                            row.id === filter.id
                              ? { ...row, value: value === '' || Number.isFinite(value) ? value : row.value }
                              : row
                          )
                        );
                      }}
                    />
                    {PERCENT_INPUT_METRICS.has(filter.metric) ? (
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-foreground-subtle">
                        %
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setHeatmapFilters((prev) => prev.filter((row) => row.id !== filter.id))
                    }
                    className="rounded-lg p-1.5 text-foreground-muted hover:bg-surface hover:text-foreground"
                    title="Remove filter"
                    aria-label="Remove filter"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {heatmapError ? (
          <p className="text-sm text-error">Failed to load heatmap data. Please try refresh.</p>
        ) : null}
        {!selectedRange.isValid ? (
          <p className="text-sm text-error">End time must be on or after start time.</p>
        ) : null}
        {heatmap.rowCount === 0 && heatmapQuerySuccess && !heatmapLoading ? (
          <p className="text-sm text-foreground-subtle">No heatmap data found for selected window.</p>
        ) : null}
        {showHeatmapTable ? (
          <div className="space-y-3" aria-busy={showHeatmapSkeleton || heatmapLoading}>
            {showHeatmapSkeleton ? (
              <div
                className="flex items-center gap-2 rounded-lg border border-border bg-page px-3 py-2 text-xs text-foreground-muted"
                role="status"
              >
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-brand" />
                <span>
                  {isAverageHeatmapView
                    ? `Loading ${averagingWeeks}-week average heatmap…`
                    : 'Loading heatmap…'}
                </span>
              </div>
            ) : null}
            <div className={`overflow-x-auto ${showHeatmapSkeleton ? 'pointer-events-none' : ''}`}>
              <table
                className={`min-w-full text-xs border border-border ${
                  showHeatmapSkeleton ? 'opacity-90' : ''
                }`}
              >
                <thead className="bg-page text-foreground-muted">
                  <tr>
                    <th
                      rowSpan={2}
                      className="sticky left-0 z-20 bg-page px-2 py-2 border-r border-b border-border text-left font-medium min-w-[74px]"
                    >
                      Hour
                    </th>
                    {filteredHeatmap.days.map((dayEntry, dayIndex) => (
                      <th
                        key={`${dayEntry.day}-${dayEntry.date}`}
                        colSpan={selectedMetricColumns.length}
                        className={`px-2 py-2 border-b border-border text-center font-medium ${HEATMAP_DAY_GROUP_BORDER}`}
                      >
                        <span>{dayEntry.day}</span>
                        <div className="text-[10px] font-normal text-foreground-subtle">
                          {showHeatmapSkeleton ? (
                            <span
                              className="mx-auto mt-1 block h-2.5 w-12 animate-pulse rounded bg-[#c8c8cc]"
                              aria-hidden
                            />
                          ) : isAverageHeatmapView ? (
                            daySampleCounts[dayIndex] ? (
                              `Avg · ${daySampleCounts[dayIndex]}w`
                            ) : (
                              'Avg · —'
                            )
                          ) : (
                            formatShortDate(dayEntry.date)
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {filteredHeatmap.days.map((dayEntry) =>
                      selectedMetricColumns.map((metric, metricIndex) => (
                        <th
                          key={`${dayEntry.day}-${dayEntry.date}-${metric.id}`}
                          className={`px-2 py-1.5 border-b border-border font-medium whitespace-nowrap ${
                            metricIndex === selectedMetricColumns.length - 1
                              ? HEATMAP_DAY_GROUP_BORDER
                              : HEATMAP_METRIC_BORDER
                          }`}
                        >
                          {metric.label}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredHeatmap.hours.map((hourEntry) => (
                    <tr key={hourEntry.hour} className="border-b border-border">
                      <td className="sticky left-0 z-10 bg-surface px-2 py-1.5 border-r border-border text-foreground-muted whitespace-nowrap">
                        {hourLabel(hourEntry.hour)}
                      </td>
                      {showHeatmapSkeleton
                        ? hourEntry.cells.flatMap((cell) =>
                            selectedMetricColumns.map((metric, metricIndex) => (
                              <HeatmapSkeletonCell
                                key={`sk-${hourEntry.hour}-${cell.date}-${metric.id}`}
                                className={
                                  metricIndex === selectedMetricColumns.length - 1
                                    ? HEATMAP_DAY_GROUP_BORDER
                                    : HEATMAP_METRIC_BORDER
                                }
                              />
                            ))
                          )
                        : hourEntry.cells.flatMap((cell) =>
                            selectedMetricColumns.map((metric, metricIndex) => (
                              <td
                                key={`${hourEntry.hour}-${cell.date}-${metric.id}`}
                                className={`px-2 py-1.5 text-right whitespace-nowrap font-medium ${
                                  metricIndex === selectedMetricColumns.length - 1
                                    ? HEATMAP_DAY_GROUP_BORDER
                                    : HEATMAP_METRIC_BORDER
                                }`}
                                style={heatmapCellStyle(
                                  metric.id,
                                  cell.aggregated[metric.id],
                                  metricRanges[metric.id],
                                  metricThresholdHigh[metric.id]
                                )}
                                title={
                                  cell.aggregated[metric.id] === null
                                    ? isAverageHeatmapView
                                      ? `${cell.day} ${hourLabel(hourEntry.hour)} · ${metric.label}: No data`
                                      : `${cell.day}, ${formatShortDate(cell.date)} ${hourLabel(hourEntry.hour)} · ${metric.label}: No data`
                                    : isAverageHeatmapView
                                      ? `${cell.day} ${hourLabel(hourEntry.hour)} · ${metric.label}: ${formatMetricValue(metric.id, cell.aggregated[metric.id])} · Avg of ${cell.rowCount} week${cell.rowCount === 1 ? '' : 's'}`
                                      : `${cell.day}, ${formatShortDate(cell.date)} ${hourLabel(hourEntry.hour)} · ${metric.label}: ${formatMetricValue(metric.id, cell.aggregated[metric.id])} · Rows: ${cell.rowCount}`
                                }
                              >
                                {formatMetricValue(metric.id, cell.aggregated[metric.id])}
                              </td>
                            ))
                          )}
                    </tr>
                  ))}
                  <tr className="border-t-2 border-border bg-page">
                    <td className="sticky left-0 z-10 bg-page px-2 py-2 border-r border-border font-semibold text-foreground whitespace-nowrap">
                      Total
                    </td>
                    {showHeatmapSkeleton
                      ? filteredHeatmap.days.flatMap((dayEntry) =>
                          selectedMetricColumns.map((metric, metricIndex) => (
                            <HeatmapSkeletonCell
                              key={`sk-total-${dayEntry.date}-${metric.id}`}
                              className={
                                metricIndex === selectedMetricColumns.length - 1
                                  ? HEATMAP_DAY_GROUP_BORDER
                                  : HEATMAP_METRIC_BORDER
                              }
                            />
                          ))
                        )
                      : filteredHeatmap.days.flatMap((dayEntry, dayIndex) => {
                          const totals = dayColumnTotals[dayIndex] ?? EMPTY_METRICS;
                          return selectedMetricColumns.map((metric, metricIndex) => (
                            <td
                              key={`total-${dayEntry.date}-${metric.id}`}
                              className={`px-2 py-2 text-right whitespace-nowrap font-semibold text-foreground ${
                                metricIndex === selectedMetricColumns.length - 1
                                  ? HEATMAP_DAY_GROUP_BORDER
                                  : HEATMAP_METRIC_BORDER
                              }`}
                              title={
                                totals[metric.id] === null
                                  ? isAverageHeatmapView
                                    ? `${dayEntry.day} · ${metric.label}: No data`
                                    : `${dayEntry.day}, ${formatShortDate(dayEntry.date)} · ${metric.label}: No data`
                                  : isAverageHeatmapView
                                    ? `${dayEntry.day} · Daily total · ${metric.label}: ${formatMetricValue(metric.id, totals[metric.id])}`
                                    : `${dayEntry.day}, ${formatShortDate(dayEntry.date)} · Daily total · ${metric.label}: ${formatMetricValue(metric.id, totals[metric.id])}`
                              }
                            >
                              {formatMetricValue(metric.id, totals[metric.id])}
                            </td>
                          ));
                        })}
                  </tr>
                </tbody>
              </table>
            </div>
            {!showHeatmapSkeleton ? (
              <>
                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-muted">
                  {selectedMetricColumns.map((metric) => {
                    const stops = heatmapLegendStops(metric.id);
                    const low = stops[0].color;
                    const high = stops[1].color;
                    return (
                      <div key={metric.id} className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-10 rounded"
                          style={{ backgroundImage: `linear-gradient(90deg, ${low}, ${high})` }}
                          aria-hidden
                        />
                        <span className="whitespace-nowrap">{metric.label}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-subtle">
                  <span>
                    Active buckets: {filteredHeatmap.activeBuckets}/{heatmapBucketTotal}
                    {activeFilterCount > 0 ? ' (filtered)' : ''}
                  </span>
                  <span>Rows loaded: {heatmap.rowCount}</span>
                </div>
              </>
            ) : (
              <p className="flex items-center gap-1.5 text-xs text-foreground-subtle" role="status">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" />
                Loading heatmap…
              </p>
            )}
          </div>
        ) : null}
      </section>

      <CreateDaypartingAutomationModal
        isOpen={createAutomationOpen}
        slotCount={prefillSummary.slotCount}
        weekdayIndexes={prefillSummary.weekdayIndexes}
        heatmapTimezone={filteredHeatmap.timezone}
        exceedsMaxWindows={prefillSummary.exceedsMaxWindows}
        campaignId={singleCampaignId}
        campaignCount={selectedCampaignIds.length}
        hasPortfolio={selectedPortfolioIds.length > 0}
        onConfirm={confirmCreateAutomation}
        onCancel={() => setCreateAutomationOpen(false)}
      />
    </div>
  );
}
